import { type CSSProperties, type ReactNode } from "react";
import { Button } from "@xpense/ui";
import { Icon, type IconName } from "./Icons";

interface PageHeaderProps {
  eyebrow?: string;
  title: string;
  description?: string;
  action?: ReactNode;
}

interface PageProps extends PageHeaderProps {
  children: ReactNode;
}

interface SectionProps {
  title?: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}

interface MetricCardProps {
  label: string;
  value: ReactNode;
  caption?: ReactNode;
  tone?: "neutral" | "success" | "danger" | "warning" | "info";
}

interface EmptyStateProps {
  icon?: IconName;
  title: string;
  description?: string;
  action?: ReactNode;
  tone?: "neutral" | "success" | "danger" | "warning";
}

export function PageHeader({ eyebrow, title, description, action }: PageHeaderProps) {
  return (
    <header className="xpn-page-header">
      <div>
        {eyebrow && <div className="xpn-page-eyebrow">{eyebrow}</div>}
        <h1 className="xpn-page-title">{title}</h1>
        {description && <p className="xpn-page-description">{description}</p>}
      </div>
      {action && <div className="xpn-page-action">{action}</div>}
    </header>
  );
}

export function Page({ eyebrow, title, description, action, children }: PageProps) {
  return (
    <div className="xpn-page xpn-fade-in">
      <PageHeader eyebrow={eyebrow} title={title} description={description} action={action} />
      {children}
    </div>
  );
}

export function Section({ title, description, action, children, className = "", style }: SectionProps) {
  return (
    <section className={`xpn-section ${className}`.trim()} style={style}>
      {(title || description || action) && (
        <div className="xpn-section-header">
          <div>
            {title && <h2 className="xpn-section-title">{title}</h2>}
            {description && <p className="xpn-section-description">{description}</p>}
          </div>
          {action && <div className="xpn-section-action">{action}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function MetricCard({ label, value, caption, tone = "neutral" }: MetricCardProps) {
  return (
    <div className={`xpn-metric-card xpn-metric-card--${tone}`}>
      <div className="xpn-metric-label">{label}</div>
      <div data-tnum className="xpn-metric-value">
        {value}
      </div>
      {caption && <div className="xpn-metric-caption">{caption}</div>}
    </div>
  );
}

export function EmptyState({
  icon = "activity",
  title,
  description,
  action,
  tone = "neutral",
}: EmptyStateProps) {
  return (
    <div className={`xpn-empty-state xpn-empty-state--${tone}`}>
      <div className="xpn-empty-state-icon">
        <Icon name={icon} />
      </div>
      <h2 className="xpn-text-heading-md xpn-mb-sm xpn-text">{title}</h2>
      {description && <p className="xpn-text-muted xpn-text-body-md">{description}</p>}
      {action && <div className="xpn-empty-state-action">{action}</div>}
    </div>
  );
}

export function RetryButton({ onClick }: { onClick: () => void }) {
  return (
    <Button onClick={onClick}>
      <Icon name="refresh" />
      Try Again
    </Button>
  );
}
